<template>
  <div class="color-selection-step">
    <div class="color-selection-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Color Options -->
      <div class="colors-grid">
        <button
          v-for="color in step.colors"
          :key="color.id"
          class="color-option"
          :class="{ selected: isColorSelected(color.id) }"
          @click="toggleColor(color.id)"
        >
          <div class="color-circle" :style="{ backgroundColor: color.hex }">
            <i class="pi pi-check check-icon" v-if="isColorSelected(color.id)"></i>
          </div>
          <span class="color-name">{{ color.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep } from '@/features/taste/types/taste-config'

interface Props {
  step: TasteStep & { type: 'color_selection' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedColors = computed(() => {
  const data = tasteStore.tasteModal.tasteData[props.step.id] || []
  // Миграция: если в store есть старые данные (string названия/ID), преобразуем их в ID
  if (data.length > 0 && typeof data[0] === 'string') {
    return data.map((nameOrId: string) => {
      const color = props.step.colors.find(
        (c) => c.name === nameOrId || c.id.toString() === nameOrId
      )
      return color?.id
    }).filter((id: any) => id !== undefined)
  }
  return data
})

function isColorSelected(colorId: number): boolean {
  return selectedColors.value.includes(colorId)
}

function toggleColor(colorId: number) {
  const currentColors = [...selectedColors.value]
  const index = currentColors.indexOf(colorId)

  if (index > -1) {
    currentColors.splice(index, 1)
  } else {
    currentColors.push(colorId)
  }

  tasteStore.updateTasteData(props.step.id, currentColors)
}

onMounted(() => {
  // Initialize with existing data if any
  if (!tasteStore.tasteModal.tasteData[props.step.id]) {
    tasteStore.updateTasteData(props.step.id, [])
  }
})
</script>

<style scoped>
.color-selection-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
  text-align: center;
}

.color-selection-content {
  max-width: 800px;
  width: 100%;
  max-height: 50vh;
  overflow-y: auto;
}

.title-section {
  margin-bottom: 32px;
  text-align: left;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.colors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 20px;
  justify-items: center;
}

.color-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid var(--grey-40);
  border-radius: 16px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 150px;
}

.color-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.15);
}

.color-option.selected {
  border-color: var(--active-100);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.2);
}

.color-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: relative;
  border: 3px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.check-icon {
  color: white;
  font-size: 24px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.color-name {
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
  text-align: center;
}

.color-option.selected .color-name {
  color: var(--active-100);
  font-weight: 600;
}

@media (max-width: 768px) {
  .colors-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 16px;
  }

  .color-option {
    max-width: 120px;
    padding: 12px;
  }

  .color-circle {
    width: 60px;
    height: 60px;
  }
}
</style>

